import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { analytics } from "@heycatch/sdk";
import { AppShell } from "@/components/AppShell";
import { ActivityBar } from "@/components/ActivityBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { JobCard } from "@/components/JobCard";
import { SharedCard } from "@/components/SharedCard";
import { SandboxSplash } from "@/components/SandboxSplash";
import { SandboxOnboarding } from "@/components/SandboxOnboarding";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useRefetchOnActive } from "@/lib/useRefetch";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Search, Loader2, FileText, Inbox, Camera, X } from "lucide-react";

const FILTERS = ["Open", "Completed", "All"];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const sub = user?.subscription;
  const active = sub?.active;
  const [shared, setShared] = useState([]);
  const [splash, setSplash] = useState(false);
  const [onboard, setOnboard] = useState(false);
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Open");
  const [q, setQ] = useState("");
  const [sheetOpen, setSheetOpen] = useState(params.get("new") === "1");
  const [form, setForm] = useState({ name: "", location: "", assignee_email: "", notes: "" });
  const [creating, setCreating] = useState(false);
  const [delJob, setDelJob] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [buying, setBuying] = useState(false);
  const [plan, setPlan] = useState("monthly");
  const [trialWarn, setTrialWarn] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(() => Number(localStorage.getItem("sp-upsell-dismiss") || 0) > Date.now() - 7 * 86400000);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const dismissBanner = () => { localStorage.setItem("sp-upsell-dismiss", String(Date.now())); setBannerDismissed(true); };
  const buy = async (selected = plan) => {
    setBuying(true);
    try { const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin, plan: selected }); window.location.href = data.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); setBuying(false); }
  };
  useEffect(() => {
    if (active && sub?.status === "trialing" && sub?.trial_days_remaining != null && sub.trial_days_remaining <= 2) {
      const k = `sp-trialwarn-${new Date().toISOString().slice(0, 10)}`;
      if (!localStorage.getItem(k)) { setTrialWarn(true); localStorage.setItem(k, "1"); }
    }
  }, [sub?.status, sub?.trial_days_remaining]);
  // Gentle, once-a-week upsell for free viewers whose trial has ended.
  useEffect(() => {
    if (active || params.get("checkout")) return;
    if (Number(localStorage.getItem("sp-upsell-seen") || 0) < Date.now() - 7 * 86400000) {
      localStorage.setItem("sp-upsell-seen", String(Date.now()));
      setUpsellOpen(true);
      try { analytics.trackEvent("free_upsell_shown"); } catch { /* ignore */ }
    }
  }, [active]); // eslint-disable-line
  const startCreate = () => { if (!active) { setUpgradeOpen(true); return; } setSheetOpen(true); };

  const toggleStatus = async (job) => {
    const next = job.status === "completed" ? "open" : "completed";
    setJobs((arr) => arr.map((x) => (x.id === job.id ? { ...x, status: next } : x)));
    try { await api.patch(`/jobs/${job.id}`, { status: next }); toast.success(next === "completed" ? "Job completed" : "Job reopened"); }
    catch { toast.error("Couldn't update — try again"); load(); }
  };
  const confirmDeleteJob = async () => {
    const job = delJob;
    setDelJob(null);
    setJobs((arr) => arr.filter((x) => x.id !== job.id));
    try { await api.delete(`/jobs/${job.id}`); toast.success("Job deleted"); }
    catch { toast.error("Delete failed"); load(); }
  };

  const archiveShared = async (s, archived) => {
    setShared((arr) => arr.map((x) => (x.token === s.token ? { ...x, archived } : x)));
    try { await api.post("/shared/archive", { token: s.token, archived }); toast.success(archived ? "Archived" : "Restored"); }
    catch { toast.error("Couldn't update — try again"); }
  };
  const deleteShared = async (s) => {
    setShared((arr) => arr.filter((x) => x.token !== s.token));
    try { await api.delete(`/shared/${s.token}`); toast.success("Removed from Shared with me"); }
    catch { toast.error("Couldn't remove — try again"); }
  };

  const load = useCallback(async () => {
    try {
      // Stale-while-revalidate: paint instantly from the IndexedDB cache, then
      // refresh in the background and update the UI when the server responds.
      // Feels like a native app (no blank wait) while staying fresh within ~1s.
      const { data } = await api.getCached("/jobs", (fresh) => setJobs(fresh || []));
      setJobs(data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Entering the sandbox from "Try it free": show a native-feeling splash, then
  // the one-time walkthrough so visitors know what to do.
  useEffect(() => {
    try {
      if (sessionStorage.getItem("sp_welcome") === "1") {
        sessionStorage.removeItem("sp_welcome");
        setSplash(true);
      }
    } catch { /* ignore */ }
  }, []);
  const splashDone = () => {
    setSplash(false);
    setOnboard(true);
  };
  const loadShared = useCallback(async () => {
    try {
      const { data } = await api.getCached("/shared", (fresh) => setShared(fresh || []));
      setShared(data || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const pend = localStorage.getItem("sp_pending_share");
        if (pend) { await api.post("/shared/attach", { token: pend }).catch(() => {}); localStorage.removeItem("sp_pending_share"); }
      } catch { /* ignore */ }
      loadShared();
    })();
  }, [loadShared]);
  const refreshAll = useCallback(() => { load(); loadShared(); }, [load, loadShared]);
  useRefetchOnActive(refreshAll);
  useEffect(() => {
    if (params.get("checkout") === "success") {
      toast.success("Trial started — you're all set!");
      analytics.trackEvent("subscription_started");
      params.delete("checkout"); setParams(params, { replace: true });
    }
  }, []); // eslint-disable-line
  // Worker just saved a shared list into a fresh free account — celebrate it and
  // offer the home-screen install so they can reopen their free account in one tap.
  useEffect(() => {
    if (params.get("saved") === "1") {
      toast.success("Saved ✓ — this list is now in your free account");
      setTimeout(() => window.dispatchEvent(new Event("sp:install-prompt")), 1000);
      params.delete("saved"); setParams(params, { replace: true });
    }
  }, []); // eslint-disable-line

  const createJob = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    const isFirstJob = jobs.length === 0;
    setCreating(true);
    try {
      const { data } = await api.post("/jobs", form);
      setSheetOpen(false);
      setForm({ name: "", location: "", assignee_email: "", notes: "" });
      // Offer the home-screen install only once they've made their first job.
      if (isFirstJob) setTimeout(() => window.dispatchEvent(new Event("sp:install-prompt")), 900);
      navigate(`/jobs/${data.id}`);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      if (e2?.response?.status === 402 && typeof d === "string" && d.startsWith("sandbox")) {
        window.dispatchEvent(new Event("sp:convert"));
      } else {
        toast.error(d || "Could not create job");
      }
    } finally { setCreating(false); }
  };

  const filtered = jobs.filter((j) => {
    if (filter === "Open" && j.status !== "open") return false;
    if (filter === "Completed" && j.status !== "completed") return false;
    if (q && !`${j.name} ${j.location || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <AppShell title="Jobs">
      <div className="px-4 pt-5 sm:px-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold" data-testid="dashboard-title">Jobs</h1>
            <p className="text-sm text-muted-foreground">{jobs.length} total</p>
          </div>
          <div className="flex items-center gap-2">
            <Button className="hidden gap-1 bg-emerald-500 hover:bg-emerald-600 md:flex" data-testid="new-job-button" onClick={startCreate}>
              <Plus className="h-4 w-4" /> New Job
            </Button>
          </div>
        </div>

        {/* search */}
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="job-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search jobs…" className="h-11 pl-9" />
        </div>

        {/* filters */}
        <div className="mt-3 flex gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.toLowerCase()}`}
              onClick={() => setFilter(f)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-150 active:scale-90 ${filter === f ? "bg-foreground text-background" : "bg-card text-muted-foreground border border-border active:bg-muted"}`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Free-viewer upsell banner — benefit-led, dismissible for 7 days */}
        {!active && !bannerDismissed && (
          <div data-testid="upgrade-banner" className="mt-4 flex items-center gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
              <Camera className="h-5 w-5" />
            </div>
            <button onClick={() => setUpgradeOpen(true)} data-testid="upgrade-banner-cta" className="flex-1 text-left">
              <span className="block text-sm font-semibold text-emerald-900 dark:text-emerald-100">Turn any checklist into photo-proof</span>
              <span className="block text-xs text-emerald-700/80 dark:text-emerald-200/70">Create &amp; send your own lists and get photos back on every item. Viewing shared lists stays free.</span>
            </button>
            <button onClick={() => setUpgradeOpen(true)} className="ml-1 hidden shrink-0 text-sm font-bold text-emerald-600 dark:text-emerald-400 sm:inline">Start creating →</button>
            <button data-testid="upgrade-banner-dismiss" aria-label="Dismiss" onClick={dismissBanner} className="ml-1 shrink-0 rounded-full p-1 text-emerald-600/60 transition-colors hover:bg-emerald-500/10 hover:text-emerald-600 dark:text-emerald-400/60">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {active && sub?.status === "trialing" && sub?.trial_days_remaining != null && sub.trial_days_remaining <= 3 && (
          <button onClick={() => setUpgradeOpen(true)} data-testid="trial-banner" className="mt-4 flex w-full items-center justify-between rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-left">
            <span className="text-sm font-medium text-amber-900 dark:text-amber-100">{sub.trial_days_remaining === 0 ? "Your free trial ends today" : `Your free trial ends in ${sub.trial_days_remaining} day${sub.trial_days_remaining === 1 ? "" : "s"}`} — subscribe to keep creating &amp; sending.</span>
            <span className="ml-3 shrink-0 text-sm font-bold text-amber-600">Subscribe →</span>
          </button>
        )}
        <AlertDialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
          <AlertDialogContent data-testid="upgrade-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{sub?.status === "trialing" ? "Keep your access" : "Your free trial has ended"}</AlertDialogTitle>
              <AlertDialogDescription>
                Subscribe to Send-Proof Pro to create and send punch lists and get photo proof back. Cancel anytime. Checking lists shared with you stays free.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="grid grid-cols-2 gap-2" data-testid="upgrade-plan-toggle">
              <button
                type="button"
                data-testid="upgrade-plan-monthly"
                onClick={() => setPlan("monthly")}
                className={`rounded-xl border p-3 text-left transition-colors ${plan === "monthly" ? "border-emerald-500 bg-emerald-500/10" : "border-border hover:border-emerald-500/40"}`}
              >
                <div className="text-sm font-bold">Monthly</div>
                <div className="text-lg font-extrabold">$9.99<span className="text-xs font-normal text-muted-foreground">/mo</span></div>
              </button>
              <button
                type="button"
                data-testid="upgrade-plan-annual"
                onClick={() => setPlan("annual")}
                className={`relative rounded-xl border p-3 text-left transition-colors ${plan === "annual" ? "border-emerald-500 bg-emerald-500/10" : "border-border hover:border-emerald-500/40"}`}
              >
                <div className="absolute -top-2 right-2 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold text-white">2 MONTHS FREE</div>
                <div className="text-sm font-bold">Annual</div>
                <div className="text-lg font-extrabold">$99<span className="text-xs font-normal text-muted-foreground">/yr</span></div>
              </button>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="upgrade-cancel">Not now</AlertDialogCancel>
              <AlertDialogAction data-testid="upgrade-buy" onClick={(e) => { e.preventDefault(); buy(); }} disabled={buying} className="bg-emerald-500 hover:bg-emerald-600">
                {buying ? <Loader2 className="h-4 w-4 animate-spin" /> : (plan === "annual" ? "Subscribe — $99/yr" : "Subscribe — $9.99/mo")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {/* Gentle one-time upsell modal for free viewers */}
        <AlertDialog open={upsellOpen} onOpenChange={setUpsellOpen}>
          <AlertDialogContent data-testid="upsell-modal">
            <AlertDialogHeader>
              <div className="mx-auto mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                <Camera className="h-6 w-6" />
              </div>
              <AlertDialogTitle className="text-center">Ready to send your own proof lists?</AlertDialogTitle>
              <AlertDialogDescription className="text-center">
                You're all set to view lists shared with you. Want to assign work and get a photo back on every item? Create and send your own in under a minute — start your Send-Proof Pro plan today.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="upsell-later">Maybe later</AlertDialogCancel>
              <AlertDialogAction
                data-testid="upsell-start"
                onClick={(e) => { e.preventDefault(); setUpsellOpen(false); setUpgradeOpen(true); try { analytics.trackEvent("free_upsell_clicked"); } catch { /* ignore */ } }}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                Start creating
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={trialWarn} onOpenChange={setTrialWarn}>
          <AlertDialogContent data-testid="trial-warn-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{sub?.trial_days_remaining === 0 ? "Your trial ends today" : `${sub?.trial_days_remaining} day${sub?.trial_days_remaining === 1 ? "" : "s"} left in your trial`}</AlertDialogTitle>
              <AlertDialogDescription>
                When your trial ends you can still open Send-Proof, but you'll need a subscription to create or send lists. Subscribe now to keep everything running.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="trial-warn-later">Remind me later</AlertDialogCancel>
              <AlertDialogAction data-testid="trial-warn-buy" onClick={(e) => { e.preventDefault(); buy(); }} disabled={buying} className="bg-emerald-500 hover:bg-emerald-600">Subscribe — $9.99/mo</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Shared with me — visually distinct from your own jobs */}
        {shared.filter((s) => (filter === "All" ? true : !s.archived)).length > 0 && (
          <div className="mt-5 rounded-2xl border border-sky-500/25 bg-sky-500/[0.06] p-3">
            <h2 className="mb-2 flex items-center gap-1.5 px-1 text-xs font-bold uppercase tracking-wide text-sky-600 dark:text-sky-300">
              <Inbox className="h-3.5 w-3.5" /> Shared with me
            </h2>
            <div className="space-y-2.5">
              {shared
                .filter((s) => (filter === "All" ? true : !s.archived))
                .map((s) => (
                  <SharedCard
                    key={s.token}
                    item={s}
                    onOpen={() => navigate(`/r/${s.token}`)}
                    onArchive={(archived) => archiveShared(s, archived)}
                    onDelete={() => deleteShared(s)}
                  />
                ))}
            </div>
          </div>
        )}

        {/* list */}
        <div className="mt-4 space-y-2.5 pb-6">
          {!loading && shared.length > 0 && filtered.length > 0 && (
            <h2 className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Your jobs</h2>
          )}
          {loading ? (
            <div className="space-y-2.5" data-testid="jobs-skeleton">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl border border-border bg-card p-4">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-5 w-40" />
                    <Skeleton className="h-5 w-12 rounded-full" />
                  </div>
                  <Skeleton className="mt-3 h-3 w-24" />
                  <Skeleton className="mt-3 h-2 w-full rounded-full" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card px-6 py-14 text-center" data-testid="empty-state">
              <FileText className="mx-auto h-10 w-10 text-muted-foreground/60" />
              <p className="mt-3 font-heading text-lg font-semibold">No jobs yet</p>
              <p className="mt-1 text-sm text-muted-foreground">Assign your first proof request in 30 seconds.</p>
              <Button className="mt-5 bg-emerald-500 hover:bg-emerald-600" data-testid="empty-create-btn" onClick={startCreate}>
                <Plus className="mr-1 h-4 w-4" /> Create Job
              </Button>
            </div>
          ) : (
            filtered.map((j) => (
              <JobCard
                key={j.id}
                job={j}
                onOpen={() => (active ? navigate(`/jobs/${j.id}`) : setUpgradeOpen(true))}
                onDelete={() => setDelJob(j)}
                onToggle={() => toggleStatus(j)}
              />
            ))
          )}
        </div>
      </div>

      {/* FAB mobile */}
      <button
        data-testid="fab-new-job"
        onClick={startCreate}
        className="fixed bottom-24 right-5 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 md:hidden"
      >
        <Plus className="h-7 w-7" />
      </button>

      <ActivityBar />

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader><SheetTitle>New Job</SheetTitle></SheetHeader>
          <form onSubmit={createJob} className="mt-4 space-y-4 pb-4">
            <div>
              <Label htmlFor="jn">Job name</Label>
              <Input id="jn" data-testid="job-name-input" autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Unit 4B Turn" className="mt-1 h-11" />
            </div>
            <div>
              <Label htmlFor="jl">Location</Label>
              <Input id="jl" data-testid="job-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Address or site" className="mt-1 h-11" />
            </div>
            <Button type="submit" disabled={creating} data-testid="job-create-submit" className="h-11 w-full bg-emerald-500 hover:bg-emerald-600">
              {creating ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create Job"}
            </Button>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete job confirm */}
      <AlertDialog open={!!delJob} onOpenChange={(o) => !o && setDelJob(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this job?</AlertDialogTitle>
            <AlertDialogDescription>This removes “{delJob?.name}” and all its reports and photos. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="job-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="job-delete-confirm" onClick={confirmDeleteJob} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {splash && <SandboxSplash onDone={splashDone} />}
      <SandboxOnboarding open={onboard} onClose={() => setOnboard(false)} />
    </AppShell>
  );
}
